
import {
  Box,
  Container,
  Grid,
  Typography,
  IconButton,
  Link,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import EmailIcon from '@mui/icons-material/Email';
import FacebookIcon from '@mui/icons-material/Facebook';
import InstagramIcon from '@mui/icons-material/Instagram';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import XIcon from '@mui/icons-material/X';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import { P } from '../theme/palette';
import { useTenantConfig } from '../hooks/useTenant';
/* =======================================
   PALETTE
   ======================================= */
const F = {
  bg: 'linear-gradient(160deg, #0c1222 0%, #111827 40%, #0f172a 100%)',
  text: '#e2e8f0',
  textMuted: '#94a3b8',
  textDim: '#64748b',
  border: 'rgba(255,255,255,0.06)',
  hoverText: '#fff',
};

/* =======================================
   STYLED
   ======================================= */
const FooterLink = styled(Link)(({ theme, islight }) => ({
  color: islight ? '#475569' : F.textMuted,
  fontSize: '0.88rem',
  textDecoration: 'none',
  cursor: 'pointer',
  fontWeight: 500,
  lineHeight: 2.2,
  transition: 'all 0.3s ease',
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  '&:hover': {
    color: islight ? '#1e40af' : '#fff',
    textDecoration: 'none',
    transform: 'translateX(3px)',
  },
}));

const SocialBtn = styled(IconButton)(({ islight }) => ({
  width: 40,
  height: 40,
  borderRadius: 12,
  color: islight ? '#475569' : F.textMuted,
  background: islight ? 'rgba(30, 64, 175, 0.04)' : 'rgba(255,255,255,0.04)',
  border: islight ? '1px solid rgba(30, 64, 175, 0.1)' : '1px solid rgba(255,255,255,0.06)',
  transition: 'all 0.35s cubic-bezier(0.4,0,0.2,1)',
  '&:hover': {
    color: islight ? '#1e40af' : '#fff',
    background: islight ? 'rgba(30, 64, 175, 0.1)' : 'rgba(255,255,255,0.1)',
    borderColor: islight ? 'rgba(30, 64, 175, 0.2)' : 'rgba(255,255,255,0.15)',
    transform: 'translateY(-3px)',
  },
}));

/* =======================================
   COMPONENT
   ======================================= */
const Footer = () => {
  const navigate = useNavigate();
  const { logo, tenantName } = useTenantConfig();

  const colors = F;

  const quickLinks = [
    { label: 'Anasayfa', path: '/' },
    { label: 'Ücretlendirme', path: '/ucretlendirme' },
    { label: 'İletişim', path: '/iletisim' },
    { label: 'Başvuru Formu', path: '/basvuru' },
  ];

  const featureLinks = [
    { label: 'Finans & Muhasebe', path: '/ozellikler/finans-muhasebe' },
    { label: 'Üye Yönetimi', path: '/ozellikler/uye-yonetimi' },
    { label: 'Web Sitesi & İçerik', path: '/ozellikler/web' },
    { label: 'Proje & Faaliyet', path: '/ozellikler/projeler' },
    { label: 'EBYS', path: '/ozellikler/ebys' },
    { label: 'Video Konferans', path: '/ozellikler/video-konferans' },
    { label: 'Toplu SMS', path: '/ozellikler/toplu-sms' },
    { label: 'Toplu Bildirim', path: '/ozellikler/toplu-bildirim' },
  ];

  const legalLinks = [
    { label: 'Gizlilik Politikası', href: '/gizlilik-politikasi' },
    { label: 'Kullanıcı Sözleşmesi', href: '/kullanici-sozlesmesi' },
    { label: 'KVKK', href: '/kvkk' },
  ];

  const socials = [
    { icon: FacebookIcon, href: '#', label: 'Facebook' },
    { icon: XIcon, href: '#', label: 'X' },
    { icon: LinkedInIcon, href: '#', label: 'LinkedIn' },
    { icon: InstagramIcon, href: '#', label: 'Instagram' },
  ];

  return (
    <Box component="footer" sx={{ position: 'relative', overflow: 'hidden' }}>

      <Box sx={{ background: colors.bg, position: 'relative' }}>
        {/* Dot grid */}
        <Box sx={{
          position: 'absolute', inset: 0, 
          opacity: 0.04,
          backgroundImage: 'radial-gradient(rgba(255,255,255,0.3) 0.5px, transparent 0.5px)',
          backgroundSize: '24px 24px', 
          pointerEvents: 'none',
        }} />

        <Container maxWidth="lg" sx={{ position: 'relative', zIndex: 2 }}>
          <Box sx={{ pt: { xs: 6, md: 8 }, pb: { xs: 4, md: 5 } }}>
            <Grid container spacing={{ xs: 4, md: 6 }}>

              {/* ─── Column 1: Brand ─── */}
              <Grid item xs={12} md={4}>
                <Box sx={{ mb: 3 }}>
                  {/* Logo/Brand */}
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2.5, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                    <img
                      src={logo.main}
                      alt={`${tenantName} Logo`}
                      style={{ height: '65px', width: 'auto' }}
                    />
                  </Box>

                  <Typography sx={{
                    color: colors.textMuted, fontSize: '0.9rem', lineHeight: 1.8, mb: 3,
                    maxWidth: 320,
                    textAlign: { xs: 'center', md: 'left' },
                  }}>
                    Dernekleriniz için kapsamlı dijital yönetim çözümü. Modern araçlarla dernek yönetiminizi kolaylaştırın.
                  </Typography>

                  {/* Social */}
                  <Box sx={{ display: 'flex', gap: 1, justifyContent: { xs: 'center', md: 'flex-start' } }}>
                    {socials.map((s, i) => (
                      <SocialBtn key={i} aria-label={s.label} href={s.href} size="small">
                        <s.icon sx={{ fontSize: 18 }} />
                      </SocialBtn>
                    ))}
                  </Box>
                </Box>
              </Grid>

              {/* ─── Column 2: Hizli Linkler ─── */}
              <Grid item xs={6} md={2}>
                <Typography sx={{
                  fontWeight: 800, fontSize: '0.8rem', color: colors.textDim,
                  textTransform: 'uppercase', letterSpacing: 1.5, mb: 2.5,
                  textAlign: { xs: 'center', md: 'left' },
                }}>
                  Hızlı Linkler
                </Typography>
                <Box sx={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: { xs: 'center', md: 'flex-start' },
                }}>
                  {quickLinks.map((link, i) => (
                    <FooterLink key={i} component="button" onClick={() => navigate(link.path)}>
                      {link.label}
                    </FooterLink>
                  ))}
                </Box>
              </Grid>

              {/* ─── Column 3: Özellikler ─── */}
              <Grid item xs={6} md={3}>
                <Typography sx={{
                  fontWeight: 800, fontSize: '0.8rem', color: colors.textDim,
                  textTransform: 'uppercase', letterSpacing: 1.5, mb: 2.5,
                  textAlign: { xs: 'center', md: 'left' },
                }}>
                  Özellikler
                </Typography>
                <Box sx={{
                  display: 'flex', flexDirection: 'column',
                  alignItems: { xs: 'center', md: 'flex-start' },
                }}>
                  {featureLinks.map((link, i) => (
                    <FooterLink key={i} component="button" onClick={() => navigate(link.path)}>
                      {link.label}
                    </FooterLink>
                  ))}
                </Box>
              </Grid>

              {/* ─── Column 4: Iletisim ─── */}
              <Grid item xs={12} md={3}>
                <Typography sx={{
                  fontWeight: 800, fontSize: '0.8rem', color: colors.textDim,
                  textTransform: 'uppercase', letterSpacing: 1.5, mb: 2.5,
                  textAlign: { xs: 'center', md: 'left' },
                }}>
                  İletişim
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
                  {/* Address */}
                  <Box sx={{
                    display: 'flex', gap: 1.5, alignItems: 'flex-start',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                  }}>
                    <Box sx={{
                      width: 34, height: 34, borderRadius: 10, flexShrink: 0, mt: 0.2,
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <LocationOnIcon sx={{ fontSize: 16, color: P.teal[400] }} />
                    </Box>
                    <Typography sx={{
                      fontSize: '0.85rem', color: colors.textMuted, lineHeight: 1.6,
                      textAlign: { xs: 'center', md: 'left' },
                    }}>
                      Güzelhisar Mah. Albay Şefik Cad. No:4 Rönesans Plaza Daire:6 Efeler/AYDIN
                    </Typography>
                  </Box>

                  {/* Phone */}
                  <Box sx={{
                    display: 'flex', gap: 1.5, alignItems: 'center',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                  }}>
                    <Box sx={{
                      width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <PhoneIcon sx={{ fontSize: 16, color: P.blue[400] }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.88rem', color: colors.textMuted, fontWeight: 500 }}>
                      0850 255 19 06
                    </Typography>
                  </Box>

                  {/* Email */}
                  <Box sx={{
                    display: 'flex', gap: 1.5, alignItems: 'center',
                    justifyContent: { xs: 'center', md: 'flex-start' },
                  }}>
                    <Box sx={{
                      width: 34, height: 34, borderRadius: 10, flexShrink: 0,
                      background: 'rgba(255,255,255,0.04)',
                      border: '1px solid rgba(255,255,255,0.06)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      <EmailIcon sx={{ fontSize: 16, color: P.teal[400] }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.88rem', color: colors.textMuted, fontWeight: 500 }}>
                      iletisim@ronesis.com.tr
                    </Typography>
                  </Box>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* ════════════════════════════════════════
              BOTTOM BAR
              ════════════════════════════════════════ */}
          <Box sx={{
            borderTop: `1px solid ${colors.border}`,
            py: 3,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
          }}>
            {/* Copyright */}
            <Typography sx={{
              fontSize: '0.82rem', color: colors.textDim, fontWeight: 500,
              order: { xs: 2, md: 1 },
            }}>
              &copy; {new Date().getFullYear()} {tenantName}. Tüm hakları saklıdır.
            </Typography>

            {/* Legal links */}
            <Box sx={{
              display: 'flex', gap: { xs: 2, md: 3 }, flexWrap: 'wrap',
              justifyContent: 'center',
              order: { xs: 1, md: 2 },
            }}>
              {legalLinks.map((link, i) => (
                <Link
                  key={i}
                  href={link.href}
                  underline="none"
                  sx={{
                    fontSize: '0.8rem', color: colors.textDim, fontWeight: 500,
                    transition: 'color 0.3s ease',
                    '&:hover': { color: '#fff' },
                  }}
                >
                  {link.label}
                </Link>
              ))}
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};

export default Footer;
